import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from '../../../context/language';
import {theme} from '../../../styles/darkTheme';
import Link from '../../atoms/Link';
import Switch from '../../atoms/Switch';
import Text from '../../atoms/Text';
import {BottomContainer, SwitchContainer, Container} from './styles';
import {parsePhoneNumber, isPossiblePhoneNumber} from 'libphonenumber-js';
import {SET_REMITTANCE} from '../../../redux/actionTypes';
import env from 'react-dotenv';
import {RootState} from '../../../redux/reducers';
import {useQuery} from '../../../hooks/useQuery';
import {setAlert} from '../../../redux/actions/global';
import * as Yup from 'yup';
import {Formik, Form} from 'formik';
import FormField from '../../molecules/FormField';
import {
  BUCKZY_POLICY, BUCKZY_TERMS,
  FIELD_CODE_REMITTANCE,
  FIELD_DESCRIPTION,
  FIELD_FULL_NAME,
  FIELD_PHONE_NUMBER,
  FIELD_REQUEST_AMOUNT,
} from '../../../utils/constants';
import PhoneField from '../../molecules/PhoneField';
import SubmitButton from '../../molecules/SubmitButton';
import axios from '../../../utils/axios';
import LoadingSpinner from '../../atoms/LoadingSpinner';
import PrivacyModal from '../../molecules/PrivacyModal';
import TermsModal from '../../molecules/TermsModal';
import { User } from '../../../models/User';

interface Form {
  fullName: string;
  phoneNumber: string;
  requestAmount: string|number;
  description: string;
  codeRemittance: string|number;
}
interface Props {
  chatName?: string;
  user?: User;
}

const RequestNewRemittance:React.FC<Props> = ({chatName = '', user: userChat}) => {
  const history = useNavigate();
  const dispatch = useDispatch();
  const query = useQuery();
  const {text} = useTranslation();

  const [accept, setAccept] = useState(false);
  const {name, amount, description, mobilePhone, countryCode, codeRemittance} = useSelector(
    (state: RootState) => ({...state.remittance}),
  );
  const {currentWatchChannel, id} = useSelector((state: RootState) => ({
    ...state.channel,
    ...state.user,
  }));
  const [phoneInfo, setPhoneInfo] = useState(
    `+${String(countryCode)}${String(mobilePhone)}`,
  );
  const getUserData = () => {
    const members = currentWatchChannel?.members;
    if (!members) return;
    for (let i = 0; i < members?.length || 0; i++) {
      const curr = members[i];
      if (curr.user_id !== id) {
        return curr.user;
      }
    }
    return null;
  };
  const currentUser = getUserData();

  const [requestPerson, setRequestPerson] = useState(currentUser?.name);
  const [requestAmount, setRequestAmount] = useState(amount);
  const [requestReason, setRequestReason] = useState(description || '');
  const [disabledInput, setDisabledInput] = useState(
    query.get('user') ? true : false,
  );
  const [doc, setDoc] = useState('');
  const [visible, setVisible] = useState(false);
  const [visibleT, setVisibleT] = useState(false);
  const [minAmount, setMinAmount] = useState<any>();
  const [maxAmount, setMaxAmount] = useState<any>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { dialCountryCode, mobileNumber} = useSelector((state: RootState) => ({
    ...state.user,
  }));
  useEffect(() => {
    (async () => {
      const url = '/api/v1/user/kyc/required_fields/?countryCode=DO';
      setIsLoading(true);
      const res = await axios.get(url);
      const {limit_max, limit_min} = res.data;
      setMaxAmount(parseInt(limit_max));
      setMinAmount(parseInt(limit_min));
      setIsLoading(false);
    })();
  }, []);

  const getTerms = async () => {
    try {
      const res = await axios.get(BUCKZY_TERMS)
      setDoc(res.data)
      setVisibleT(true)
    } catch (e) {
      console.log(e)
    }
  }

  const getPolicy = async () => {
    try {
      const res = await axios.get(BUCKZY_POLICY)
      setDoc(res.data)
      setVisible(true)
    } catch (e) {
      console.log(e)
    }
  }

  const defaultValues: Form = {
    fullName: currentUser?.name,
    phoneNumber: mobilePhone,
    requestAmount,
    description,
    codeRemittance,
  };

  const validationSchema = Yup.object().shape({
    fullName: Yup.string()
      .required(text.requiredField(text.personRequest))
      .label(text.personRequest),
    phoneNumber: Yup.string()
      .required(text.requiredField(text.phoneNumber))
      .label(text.phoneNumber),
    requestAmount: Yup.number()
      .required(text.requiredField(text.amountRequest))
      .min(minAmount, text.minAmount(minAmount))
      .max(maxAmount, text.maxAmount(maxAmount))
      .label(text.amountRequest),
    description: Yup.string().label(text.reasonRequest),
    codeRemittance: Yup.number().integer().nullable()
        .required(text.requiredField(text.codeRemittance))
        .label(text.codeRemittance),
  });

  const formatPhoneNumber = (phoneNumber: string) => {
    return phoneNumber.charAt(0) === '+' ? phoneNumber : `+1${phoneNumber}`;
  };

  const formatAmount = (amount: number | string) => {
    return typeof amount === 'string'
      ? parseFloat(amount).toFixed(2)
      : amount.toFixed(2);
  };

  const handleSubmit = (values: Form) => {
    if (!accept) return;
    const {fullName, phoneNumber, requestAmount, description, codeRemittance,} = values;
    const formattedPhoneNumber = formatPhoneNumber(phoneNumber);
    if (isPossiblePhoneNumber(formattedPhoneNumber || '')) {
      const userNumber = parsePhoneNumber(formattedPhoneNumber || '');
      if (!userNumber.country) {
        return dispatch(
          setAlert({title: text.error, message: text.invalidPhoneNumber}),
        );
      }
      const dataToSend = {
        senderFullName: '',
        senderPhone: {
          dialCountryCode: userNumber.countryCallingCode,
          mobileNumber: userNumber.nationalNumber,
        },
        recipientPhone: {
          dialCountryCode: dialCountryCode,
          mobileNumber: mobileNumber,
        },
        amount: formatAmount(requestAmount),
        description,
        codeRemittance,
      };
      currentWatchChannel?.members.forEach(member => {
        const currId = member.user?.id;
        if (currId === id) {
          dataToSend.senderFullName = fullName;
          // dataToSend.codeRemittance = ;
        }
      });
      if (dataToSend.senderFullName == '') {
        dataToSend.senderFullName = fullName
      }

      dispatch({ type: SET_REMITTANCE, payload: dataToSend });
      history('/confirm');
    } else {
      dispatch(
        setAlert({title: text.error, message: text.invalidPhoneNumber}),
      );
    }
  };



  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Container>
      <PrivacyModal content={doc} visible={visible} setVisible={setVisible} />
      <TermsModal content={doc} visible={visibleT} setVisible={setVisibleT} />
      <Formik
        initialValues={defaultValues}
        validationSchema={validationSchema}
        onSubmit={values => {
          handleSubmit(values);
        }}>
        {() => (
          <Form>
            <FormField
              type={'text'}
              label={text.personRequest}
              placeholder={text.namePlaceholder}
              name={FIELD_FULL_NAME}
              defaultValue={requestPerson}
              disabled={disabledInput}
            />
            <PhoneField
              placeholder={text.phoneNumber}
              name={FIELD_PHONE_NUMBER}
              disabled={disabledInput}
              value={phoneInfo}
            />
            <FormField
              type={'number'}
              label={text.amountRequest}
              placeholder={text.amountPlaceholder}
              name={FIELD_REQUEST_AMOUNT}
              defaultValue={requestAmount}
            />
            <FormField
                type={'number'}
                label={text.codeRemittance}
                placeholder={text.codeRemittance}
                name={FIELD_CODE_REMITTANCE}
                defaultValue={codeRemittance}
                integer={true}
            />
            <FormField
              type={'text'}
              label={text.reasonRequest}
              placeholder={text.reasonPlaceholder}
              name={FIELD_DESCRIPTION}
              defaultValue={requestReason}
            />
            <BottomContainer>
              <SwitchContainer>
                <Switch theme={theme} enabled={accept} setEnabled={setAccept} />
                <Text
                  fontColor={theme.darkText}
                  fontSize="14px"
                  lineHeight="20px">
                  {text.readTerms}
                  <Link
                    onClick={() =>
                      getTerms()
                    }
                    fontColor={theme.primary}
                    fontSize="14px"
                    fontWeight="bold"
                    lineHeight="20px">
                    {text.termsOfUse}
                  </Link>
                  {text.termsAnd}
                  <Link
                    onClick={() =>
                      getPolicy()
                    }
                    fontColor={theme.primary}
                    fontSize="14px"
                    fontWeight="bold"
                    lineHeight="20px">
                    {text.privacyPolicy}
                  </Link>
                  {text.ofTigo}
                </Text>
              </SwitchContainer>
              <SubmitButton title={text.request} />
            </BottomContainer>
          </Form>
        )}
      </Formik>
    </Container>
  );
};

export default RequestNewRemittance;
